import { useEffect, useState } from "react";
import type { Project } from "@makeit/schema";
import { loadProject, sourceFromLocation, LoadError, type ProjectSource } from "./data/loadProject";
import { ensureLibraryManifest } from "./data/media";
import { Player } from "./player/Player";
import { Home } from "./ui/Home";
import { resetLanguage, t } from "./player/i18n";
import { loadStudent } from "./data/student";
import { forgetSharedCode, rememberSharedCode } from "./data/sharedCodes";
import { IS_PLAY_SITE } from "./site";

type State =
  | { status: "home"; error?: string }
  | { status: "loading"; source: ProjectSource }
  | { status: "ready"; project: Project; source: ProjectSource }
  | { status: "error"; message: string };

export function App() {
  const [state, setState] = useState<State>(() => {
    const source = sourceFromLocation();
    return source ? { status: "loading", source } : { status: "home" };
  });

  useEffect(() => {
    if (state.status !== "loading") return;
    let cancelled = false;
    Promise.all([loadProject(state.source), ensureLibraryManifest()])
      .then(([project]) => {
        if (cancelled) return;
        if (IS_PLAY_SITE && state.source.kind === "code") rememberSharedCode(state.source.code, project);
        setState({ status: "ready", project, source: state.source });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // a code that no longer finds its project leaves «Shared with me»
        if (IS_PLAY_SITE && state.source.kind === "code" && err instanceof LoadError && err.reason === "not-found") forgetSharedCode(state.source.code);
        const message = err instanceof LoadError ? messageFor(err) : t("errGeneric");
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  const play = (source: ProjectSource) => {
    const q = new URLSearchParams();
    if (source.kind === "code") q.set("code", source.code);
    if (source.kind === "project") q.set("project", source.projectId);
    if ((source.kind === "project" || source.kind === "inline" || source.kind === "demo") && source.testFrom != null) q.set("test", String(source.testFrom));
    if (source.kind === "demo") q.set("demo", `${source.lang}/${source.id}`);
    if (source.kind !== "inline") history.pushState(null, "", `?${q.toString()}${location.hash}`);
    setState({ status: "loading", source });
  };

  const exit = () => {
    // the play site's old /play.html and /mobile.php links go back to its home
    history.pushState(null, "", (IS_PLAY_SITE ? "/" : location.pathname) + location.hash);
    resetLanguage();
    setState({ status: "home" });
  };

  switch (state.status) {
    case "home":
      return <Home onPlay={play} error={state.error} />;
    case "loading":
      return (
        <div className="screen loading">
          <img src="/brand/m.png" alt="" width={90} height={90} />
          <div className="spinner" />
          <p className="muted">{t("loading")}</p>
        </div>
      );
    case "error":
      return <Home onPlay={play} error={state.message} />;
    case "ready":
      return (
        <Player
          project={state.project}
          onExit={exit}
          localAnswers={state.source.kind === "demo" && new URLSearchParams(location.search).get("answers") !== "cloud"}
          testFrom={state.source.kind === "project" || state.source.kind === "inline" || state.source.kind === "demo" ? state.source.testFrom : undefined}
          student={location.hash.startsWith("#/student") ? studentFor(loadStudent()) : null}
        />
      );
  }
}

function studentFor(s: ReturnType<typeof loadStudent>) {
  return s ? { classroomId: s.classroomId, studentId: s.studentId, name: s.name } : null;
}

function messageFor(err: LoadError): string {
  switch (err.reason) {
    case "not-found":
      return t("errNotFound");
    case "server":
      return t("errServer");
    case "invalid":
      return t("errInvalid");
    default:
      return t("errNetwork");
  }
}
